import React, { useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	main_softwareCodePage_FilteredTable$key,
	main_softwareCodePage_FilteredTable$data,
} from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_FilteredTable.graphql';
import { UNKNOWN_ID } from '../../../../../../common/utils/filters/constants';
import {
	usePullRequestTableOptionsStore,
	useAppliedPullRequestFilters,
} from '../../../../../../controllers/pull-request-filters';
import PullRequestTable from './pull-request-table';

type ArrElement<ArrType> = ArrType extends readonly (infer ElementType)[] ? ElementType : never;
type PullRequestData = ArrElement<main_softwareCodePage_FilteredTable$data>;

type Props = {
	filteredTableData: main_softwareCodePage_FilteredTable$key;
	isLoading: boolean;
};

export const summaryFilterFn = (pullRequest: PullRequestData, summaryFilter: string): boolean =>
	(pullRequest.title ?? '').toLowerCase().includes(summaryFilter.toLowerCase());

export const authorsFilterFn = (pullRequest: PullRequestData, authorsFilter: string[]): boolean => {
	if (authorsFilter.length === 0) {
		return true;
	}

	return pullRequest.author
		? authorsFilter.includes(pullRequest.author.accountId)
		: authorsFilter.includes(UNKNOWN_ID);
};

export const reviewersFilterFn = (
	pullRequest: PullRequestData,
	reviewersFilter: string[],
): boolean => {
	if (reviewersFilter.length === 0) {
		return true;
	}

	// When there is at least one filter set, we're not keeping PRs with no reviewers
	if (pullRequest.reviewers == null || pullRequest.reviewers.length === 0) {
		return false;
	}

	const reviewerList = pullRequest.reviewers.map((reviewer) => reviewer?.user);
	return reviewerList.some((reviewerUser) =>
		reviewerUser
			? reviewersFilter.includes(reviewerUser.accountId)
			: reviewersFilter.includes(UNKNOWN_ID),
	);
};

export const statusFilterFn = (pullRequest: PullRequestData, statusFilter: string[]): boolean => {
	if (statusFilter.length === 0) {
		return true;
	}

	return pullRequest.status ? statusFilter.includes(pullRequest.status) : false;
};

export const repositoriesFilterFn = (
	pullRequest: PullRequestData,
	repositoriesFilter: string[],
): boolean => {
	if (repositoriesFilter.length === 0) {
		return true;
	}

	return pullRequest.repositoryId
		? repositoriesFilter.includes(pullRequest.repositoryId)
		: repositoriesFilter.includes(UNKNOWN_ID);
};

export function applyFilters<T>(
	filtersFns: ((pr: T) => boolean)[],
	data: ReadonlyArray<T>,
): ReadonlyArray<T> {
	return filtersFns.reduce<ReadonlyArray<T>>((list, filterFn) => list.filter(filterFn), data);
}

export const FilteredTable = ({ filteredTableData, isLoading }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [pullRequestTableOptions] = usePullRequestTableOptionsStore();
	const [appliedFilters] = useAppliedPullRequestFilters();

	const previousPullRequestTableOptions = usePreviousWithInitial(pullRequestTableOptions);

	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_FilteredTable on DevOpsPullRequestDetails
			@relay(plural: true) {
				title
				author {
					accountId
				}
				reviewers {
					user {
						accountId
					}
				}
				status
				repositoryId
				...pullRequestTable_softwareCodePage
			}
		`,
		filteredTableData,
	);

	const filteredPrList = useMemo(() => {
		const filtersFns = [
			(pr: PullRequestData) => summaryFilterFn(pr, pullRequestTableOptions.titleFilter),
			(pr: PullRequestData) => authorsFilterFn(pr, pullRequestTableOptions.authorsFilter),
			(pr: PullRequestData) => reviewersFilterFn(pr, pullRequestTableOptions.reviewersFilter),
			(pr: PullRequestData) => statusFilterFn(pr, pullRequestTableOptions.statusFilter),
			(pr: PullRequestData) => repositoriesFilterFn(pr, pullRequestTableOptions.repositoryFilter),
		];
		return applyFilters<PullRequestData>(filtersFns, data);
	}, [data, pullRequestTableOptions]);

	useEffect(() => {
		if (pullRequestTableOptions !== previousPullRequestTableOptions) {
			fireUIAnalytics(
				createAnalyticsEvent({ action: 'updated', actionSubject: 'filters' }),
				'pullRequestList',
				{
					searchApplied: appliedFilters.titleFilter,
					repositoryFilters: appliedFilters.repositoryFilter,
					statusFilters: appliedFilters.statusFilter,
					authorFilters: appliedFilters.authorsFilter,
					reviewerFilters: appliedFilters.reviewersFilter,
					filteredPullRequestCount: filteredPrList.length,
					sortKey: pullRequestTableOptions.sortKey,
					sortDirection: pullRequestTableOptions.sortOrder,
				},
			);
		}
	}, [
		appliedFilters.titleFilter,
		createAnalyticsEvent,
		pullRequestTableOptions,
		filteredPrList.length,
		previousPullRequestTableOptions,
		appliedFilters.repositoryFilter,
		appliedFilters.statusFilter,
		appliedFilters.authorsFilter,
		appliedFilters.reviewersFilter,
		pullRequestTableOptions.sortKey,
		pullRequestTableOptions.sortOrder,
	]);

	return <PullRequestTable pullRequestTableData={filteredPrList} isLoading={isLoading} />;
};
